import { create } from 'zustand';
import { api } from '../lib/api';
import { setAnalyticsConsent, identify, reset as resetAnalytics, track } from '../lib/analytics';
import { setSentryUser } from '../lib/sentry';
import { isImpersonating as impActive, getImpersonationExpiresAt, exitImpersonation } from '../lib/impersonation';

// Sprint 28c — RBAC v2 (8 rol hiyerarşisi).
// Eski ADMIN → OWNER, eski MANAGER → DEPARTMENT_MANAGER migrate edildi.
export type UserRole =
  | 'OWNER'
  | 'CEO'
  | 'CFO'
  | 'ACCOUNTANT'
  | 'DEPARTMENT_MANAGER'
  | 'TEAM_LEAD'
  | 'EMPLOYEE'
  | 'EXPENSE_ONLY';
export type CompanyStatus = 'PENDING_KYB' | 'ACTIVE' | 'SUSPENDED';

export interface AuthUser {
  id: string;
  email: string;
  fullName: string;
  role: UserRole;
  /** Sprint 28c — dış kullanıcı (muhasebeci/denetçi); read-only. */
  isExternal?: boolean;
  /** Sprint 28c — direkt yönetici user id (zincir izlemek için). */
  managerId?: string | null;
  department?: string | null;
  /** Sprint 18 Block C — telefon doğrulama zaman damgası (/auth/me döndürür). */
  phoneVerifiedAt?: string | null;
  company: {
    id: string;
    legalName: string;
    taxNumber: string;
    status: CompanyStatus;
  };
}

interface AuthState {
  user: AuthUser | null;
  isLoading: boolean;
  hasCheckedSession: boolean;

  /** Sprint 34 — platform staff bir müşteriyi salt-okunur görüntülüyor mu? */
  isImpersonating: boolean;
  /** İmpersone edilen şirketin yasal unvanı (banner'da gösterilir). */
  impersonatedCompanyName: string | null;
  /** İmpersonation token'ının exp claim'i (ms epoch) — geri sayım için. */
  impersonationExpiresAt: number | null;

  fetchSession: () => Promise<void>;
  setUser: (user: AuthUser | null) => void;
  logout: () => Promise<void>;
  exitImpersonation: () => void;
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  isLoading: false,
  hasCheckedSession: false,
  isImpersonating: false,
  impersonatedCompanyName: null,
  impersonationExpiresAt: null,

  async fetchSession() {
    set({ isLoading: true });
    const impersonating = impActive();
    try {
      const user = await api.get<AuthUser>('/auth/me');
      set({
        user,
        isLoading: false,
        hasCheckedSession: true,
        isImpersonating: impersonating,
        impersonatedCompanyName: impersonating ? user.company.legalName : null,
        impersonationExpiresAt: impersonating ? getImpersonationExpiresAt() : null,
      });
      // Sprint 17 T17.18 — Sentry user context (sadece UUID + role, email ASLA)
      setSentryUser({ id: user.id, companyId: user.company.id, role: user.role });
      // İmpersonation modunda analytics çalıştırma (gerçek kullanıcı aktivitesi değil).
      if (impersonating) {
        setAnalyticsConsent(false);
        return;
      }
      // KVKK consent durumuna göre analytics aç/kapa
      try {
        const c = await api.get<{ consents: { attOptIn?: boolean } }>('/kvkk/consents');
        const optIn = !!c.consents.attOptIn;
        setAnalyticsConsent(optIn);
        if (optIn) {
          await identify({
            userIdHash: await sha256(user.id),
            companyIdHash: await sha256(user.company.id),
            role: user.role,
          });
          await track('pilot.feature_used', { feature: 'web_admin_login', success: true });
        }
      } catch {
        setAnalyticsConsent(false);
      }
    } catch {
      set({
        user: null,
        isLoading: false,
        hasCheckedSession: true,
        isImpersonating: false,
        impersonatedCompanyName: null,
        impersonationExpiresAt: null,
      });
      setSentryUser(null);
    }
  },

  setUser(user) {
    set({ user, hasCheckedSession: true });
    if (user) {
      setSentryUser({ id: user.id, companyId: user.company.id, role: user.role });
    } else {
      setSentryUser(null);
    }
  },

  async logout() {
    try {
      await api.post('/auth/logout');
    } catch {
      // ignore — yine de cookie'leri temizle
    }
    await resetAnalytics();
    setAnalyticsConsent(false);
    setSentryUser(null);
    set({ user: null });
  },

  // Sprint 34 — impersonation'dan çık: token'ı temizle + platform konsoluna dön.
  exitImpersonation() {
    set({ isImpersonating: false, impersonatedCompanyName: null, impersonationExpiresAt: null, user: null });
    exitImpersonation();
  },
}));

async function sha256(input: string): Promise<string> {
  const data = new TextEncoder().encode(input);
  const buf = await crypto.subtle.digest('SHA-256', data);
  return Array.from(new Uint8Array(buf))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('')
    .slice(0, 16);
}
